/* eslint-disable no-undef */
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { dataDogInit } from 'helpers/dataDog';

import {
  AnalyticsManager,
  ApiManager,
  Atlas,
  Bus,
  DetectionProvider,
  ExtensionsManager,
  HistoryManager,
  initDebug,
  isNotEmptyObject,
  NotificationManager,
  SettingsManager,
  SiteConfigManager,
  StaticBlocksProvider,
  StorageManager,
  SubscriptionManager,
  TranslationProvider,
} from '@atlas/core';
import { clientProfileRoutes, paymentRoutes, staticRoutes } from '@atlas/routing';

import { BTAG_STORAGE_KEY, CXFROM_STORAGE_KEY } from 'common/constants';
import Notifications from 'common/Notifications';
import { CDN_TYPE_SPORT, getCDNUrl, LocalConfig } from 'common/utils';
import history from 'components/Layout/history';
import SignUpLayout from 'components/Layout/SignUpLayout';
import GlobalMaintenanceContainer from 'components/MaintenanceContainer/GlobalMaintenanceContainer';
import ToastContainer from 'components/Toast/ToastContainer';
import { LayoutLoader } from 'src/components/Layout/LayoutLoader';

import 'common/Form/formConfig';
import '../subscriptions/dataLayer.bindings';

import packageData from '../../package.json';
import { resolveFingerprint } from '../fingerprint';
import busEventsMapping from '../subscriptions/bus.bindings';
import subscribeUserOffline from '../subscriptions/subscribeUserOffline';

const ShareCss = React.lazy(() => import('modules/share-css'));

dataDogInit();
initDebug();

const SuspenseCssComponent = () => (
  <Suspense fallback={<span style={{ display: 'none' }} />}>
    <ShareCss />
  </Suspense>
);

// Updating application default settings using local config
const localLanguage = LocalConfig.get('language');
if (localLanguage) {
  SettingsManager.settings.lang = localLanguage;
}

// Only for development
if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line import/extensions, global-require
  const localCmsSettings = require('./cms.settings.js');
  SiteConfigManager.setConfig(localCmsSettings.default);

  // Loading svg sprites from dyeing
  fetch('/dyeing/build/sprite/sprite.svg')
    .then(res => res.text())
    .then(svg => {
      document.body.insertAdjacentHTML('beforeend', svg);
    });
}

// Only for production
if (process.env.NODE_ENV === 'production') {
  // Applying default client application settings
  if (isNotEmptyObject(window.CMS_CONFIG)) {
    const { navigation = {}, page = {} } = window.CMS_CONFIG;

    // FIXME
    SiteConfigManager.setConfig({
      ...page,
      navigation,
    });
  }
}

// Set simple login
SettingsManager.updateSettings({
  isSimpleLogin: true,
});

// Resolving stored settings
SettingsManager.resolveSettings();

busEventsMapping(Bus, history, {
  // Overriding default registration success process that should be turned off
  // for optimized signup
  [Bus.events.auth.formRegistrationSuccess]: () => {},
});
subscribeUserOffline();

// Initializing api default headers
const defaultHeaders = {
  device: 'desktop',
  version: packageData.version,
};

const projectId = LocalConfig.get('projectId');

// Providing project id if it exist in config
if (projectId) {
  defaultHeaders['x-project-id'] = projectId;
}

// Providing header for local testing
if (process.env.NODE_ENV === 'development') {
  defaultHeaders['x-project-team'] = 'frontend-team';
}

SiteConfigManager.device = 'desktop';
// Setting application version & device type
ApiManager.setDefaultHeaders(defaultHeaders);

const { host, protocol } = window.location;

ApiManager.setHostname('core', `${protocol}//${host}/api/`);
ApiManager.setHostname('cms', `${protocol}//${host}/api/cms-go/v1/site/`);
ApiManager.setHostname('cms/v2', `${protocol}//${host}/api/cms-go/v2/site/`);
ApiManager.setHostname('cdn', getCDNUrl(CDN_TYPE_SPORT));

if (process.env.NODE_ENV === 'development') {
  window.Bus = Bus;

  window.NotificationManager = NotificationManager;
  window.SiteConfigManager = SiteConfigManager;
  window.HistoryManager = HistoryManager;
  window.SubscriptionManager = SubscriptionManager;
  window.SettingsManager = SettingsManager;
}

// Creating atlas extensions manager
window.aem = new ExtensionsManager();

// Initializing analytics
AnalyticsManager.init({
  fingerprint: () =>
    resolveFingerprint().then(components => window.btoa(JSON.stringify(components))),
});

const domNode = document.getElementById('root');
const root = createRoot(domNode);

root.render(
  <>
    <SuspenseCssComponent />
    <Atlas>
      <TranslationProvider>
        <StaticBlocksProvider>
          <DetectionProvider>
            <GlobalMaintenanceContainer>
              <LayoutLoader>
                <SignUpLayout optimized={true} />
                <ToastContainer />
                <Notifications />
              </LayoutLoader>
            </GlobalMaintenanceContainer>
          </DetectionProvider>
        </StaticBlocksProvider>
      </TranslationProvider>
    </Atlas>
  </>,
);
